/**
 * Action Assurance Types — Severity-based governance status for platform-wide UX.
 *
 * The 7 Laws of AIdentity are the architectural framework.
 * They are NOT the user-facing vocabulary. Operators think in outcomes:
 * "Can I trust this action?" — not in law numbers.
 *
 * User-facing statuses (severity-based, not count-based):
 * - verified: Complete required evidence for this action class
 * - limited: Partial evidence, no direct failure
 * - needs_review: Missing critical fields (actor chain, tool publisher, etc.)
 * - failed: Receipt verification failed, authorization denied, schema drift
 * - unavailable: Evidence collection not enabled for this agent type
 */

export type AssuranceStatus =
  | 'verified'
  | 'limited'
  | 'needs_review'
  | 'failed'
  | 'unavailable';

export type AssuranceCardName =
  | 'identity'
  | 'intent'
  | 'delegation'
  | 'authorization'
  | 'exposure'
  | 'tool_integrity'
  | 'proof';

export type AssuranceContext =
  | 'chat'           // ai-assistant: minimal, hide when verified
  | 'run_detail'     // agent-hub: full detail
  | 'review_task'    // access-governance: reviewer decision frame
  | 'investigation'; // access-risk: investigation-oriented

export interface AssuranceIssue {
  severity: 'failed' | 'needs_review' | 'limited';
  card: AssuranceCardName;
  summary: string;
  field?: string;
  law?: string;
}

export interface LawCoverageEntry {
  status: 'complete' | 'partial' | 'none' | 'n/a';
  missing: string[];
}

export interface SplitActorRecord {
  delegator?: string;
  agent?: string;
  worker?: string;
  credential_subject?: string;
  downstream?: string;
  tenant?: string;
}

export interface DelegationSnapshot {
  delegation_id?: string;
  scope_label?: string;
  trust_level?: string;
  budget_total_usd?: number;
  budget_spent_usd?: number;
  budget_remaining_pct?: number;
  expires_at?: string;
  constraints?: Record<string, unknown>;
}

export interface ExposureSummary {
  tools_visible_count: number;
  tools_used_count: number;
  capabilities_granted?: number;
  capabilities_exercised?: number;
  exposure_ratio: number;
}

export interface AuthorizationMoment {
  moment: string; // gateway | discovery | invocation | execution
  decision: 'allowed' | 'denied' | 'step_up' | 'not_recorded';
  at?: string;
  decision_id?: string;
}

export interface ActionAssuranceData {
  status: AssuranceStatus;
  reason: string;
  risk_tier: string;
  law_coverage: Record<string, LawCoverageEntry>;
  issues: AssuranceIssue[];
  summary_sentence: string;
  evidence_schema_version: string;
}

export interface ActionAssuranceProofPack {
  action_assurance?: ActionAssuranceData;
  split_actor?: SplitActorRecord;
  delegation_snapshot?: DelegationSnapshot;
  exposure?: ExposureSummary;
  authorization_moments?: Record<string, AuthorizationMoment>;
}

/**
 * Feature detection: does the proof-pack contain governance data?
 */
export function hasAssuranceData(
  proofPack: ActionAssuranceProofPack | null | undefined,
): boolean {
  if (!proofPack) return false;
  return proofPack.action_assurance != null;
}

/**
 * Maps AssuranceStatus to CSS color variable names.
 */
export function assuranceStatusColor(status: AssuranceStatus): string {
  switch (status) {
    case 'verified': return 'var(--color-success)';
    case 'limited': return 'var(--color-text-secondary)';
    case 'needs_review': return 'var(--color-warning)';
    case 'failed': return 'var(--color-error)';
    case 'unavailable': return 'var(--color-text-disabled)';
  }
}

/**
 * User-facing copy for each status.
 */
export const ASSURANCE_COPY: Record<AssuranceStatus, {
  label: string;
  tooltip: string;
}> = {
  verified: {
    label: 'Verified',
    tooltip: 'Action fully assured',
  },
  limited: {
    label: 'Limited assurance',
    tooltip: 'Partial evidence — no direct failure',
  },
  needs_review: {
    label: 'Needs review',
    tooltip: 'Missing critical evidence',
  },
  failed: {
    label: 'Failed',
    tooltip: 'Evidence verification failed',
  },
  unavailable: {
    label: 'Evidence unavailable',
    tooltip: 'Evidence collection not enabled',
  },
};

/**
 * Card display labels (operational language, not law numbers).
 */
export const CARD_LABELS: Record<AssuranceCardName, string> = {
  identity: 'Identity',
  intent: 'Intent',
  delegation: 'Delegation',
  authorization: 'Authorization',
  exposure: 'Exposure',
  tool_integrity: 'Tool Integrity',
  proof: 'Proof',
};
